// hooks/signalr/useEditLocks.js
import { useState, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { LOCK_CONFIG } from './signalRConfig';

export const useEditLocks = (connectionRef) => {
  const [editLocks, setEditLocks] = useState({});
  const [myLocks, setMyLocks] = useState({});
  const lockRenewalTimers = useRef({});

  // ========== LOCK RENEWAL ==========
  const startLockRenewal = useCallback((lockId, entityId) => {
    if (lockRenewalTimers.current[entityId]) {
      clearInterval(lockRenewalTimers.current[entityId]);
    }

    lockRenewalTimers.current[entityId] = setInterval(async () => {
      try {
        if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
          const renewed = await connectionRef.current.invoke('RenewLock', lockId);
          if (!renewed) {
            stopLockRenewal(entityId);
            setMyLocks(prev => {
              const newLocks = { ...prev };
              delete newLocks[entityId];
              return newLocks;
            });
          }
        }
      } catch (err) {
        console.error('Error renewing lock:', err);
      }
    }, LOCK_CONFIG.renewalInterval);
  }, [connectionRef]);

  const stopLockRenewal = useCallback((entityId) => {
    if (lockRenewalTimers.current[entityId]) {
      clearInterval(lockRenewalTimers.current[entityId]);
      delete lockRenewalTimers.current[entityId];
    }
  }, []);

  // ========== REQUEST LOCK ==========
  const requestEditLock = useCallback(async (station, date, entityId) => {
    try {
      if (connectionRef.current?.state !== signalR.HubConnectionState.Connected) {
        throw new Error('Not connected');
      }

      const result = await connectionRef.current.invoke(
        'RequestEditLock',
        station,
        date,
        entityId
      );

      if (result.success) {
        setMyLocks(prev => ({
          ...prev,
          [entityId]: {
            lockId: result.lockId,
            expiresAt: result.expiresAt,
            entityId
          }
        }));

        startLockRenewal(result.lockId, entityId);
        return { success: true };
      } else {
        return {
          success: false,
          lockedBy: result.lockedBy,
          lockedUntil: result.lockedUntil,
          message: result.message
        };
      }
    } catch (err) {
      console.error('Error requesting lock:', err);
      return { success: false, error: err.message };
    }
  }, [connectionRef, startLockRenewal]);

  // ========== RELEASE LOCK ==========
  const releaseLock = useCallback(async (station, date, entityId) => {
    try {
      if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
        await connectionRef.current.invoke('ReleaseLock', station, date, entityId);
        stopLockRenewal(entityId);
        setMyLocks(prev => {
          const newLocks = { ...prev };
          delete newLocks[entityId];
          return newLocks;
        });
      }
    } catch (err) {
      console.error('Error releasing lock:', err);
    }
  }, [connectionRef, stopLockRenewal]);

  // ========== RELEASE ALL LOCKS ==========
  const releaseAllLocks = useCallback(async (station, date) => {
    try {
      if (connectionRef.current?.state === signalR.HubConnectionState.Connected) {
        await connectionRef.current.invoke('ReleaseAllLocks', station, date);

        // Stoppa alla renewal timers
        Object.keys(lockRenewalTimers.current).forEach(entityId => {
          stopLockRenewal(entityId);
        });

        setMyLocks({});
      }
    } catch (err) {
      console.error('Error releasing all locks:', err);
    }
  }, [connectionRef, stopLockRenewal]);

  // ========== CLEANUP ==========
  const cleanupLocks = useCallback(() => {
    Object.keys(lockRenewalTimers.current).forEach(entityId => {
      stopLockRenewal(entityId);
    });
  }, [stopLockRenewal]);

  return {
    editLocks,
    myLocks,
    setEditLocks,
    setMyLocks,
    requestEditLock,
    releaseLock,
    releaseAllLocks,
    cleanupLocks
  };
};